/**
 * Data-free YouTube helpers: URLs, formats, labels. Client components import
 * from here, never from `@/data/youtube-videos`, so no page ships the whole
 * video registry (every transcript) in its JavaScript. youtube-videos.test.ts
 * fails when a 'use client' file imports the registry.
 */

export const youtubeWatchUrl = (id: string) =>
  `https://www.youtube.com/watch?v=${id}`

/** Privacy-mode player. Only this host is allowed in the CSP frame-src. */
export const youtubeEmbedUrl = (id: string) =>
  `https://www.youtube-nocookie.com/embed/${id}`

export const youtubeThumbnailUrl = (id: string) =>
  `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`

/** 84 → "1:24" */
export const formatVideoLength = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

/** 84 → "PT1M24S" (schema.org duration) */
export const isoVideoDuration = (seconds: number) =>
  `PT${Math.floor(seconds / 60) ? `${Math.floor(seconds / 60)}M` : ''}${seconds % 60 ? `${seconds % 60}S` : ''}`

/** YouTube's upload date as shown on YouTube: the calendar date stored in
 *  `uploadDate`, never shifted into another time zone. */
export const formatUploadDate = (uploadDate: string) =>
  new Date(`${uploadDate.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })

/** How the transcript is labelled: a video spoken in another language
 *  carries its English subtitles, which are a translation, not the spoken
 *  text. */
export const transcriptLabel = (v: { language?: string }) =>
  v.language ? 'Transcript (English subtitles)' : 'Transcript'

/** A long-form video's page on the site. */
export const videoPagePath = (v: { slug: string }) => `/videos/${v.slug}`

/** The fields an on-page video section shows, passed from a server page to a
 *  client component instead of the whole registry. */
export interface VideoEmbed {
  id: string
  title: string
  slug: string
  durationSeconds: number
  summary: string
  note?: string
  /** Only for sections that show it; a link-only section leaves it out. */
  transcript?: string
  language?: string
}
